import { FC, ReactNode } from 'react';
import dynamic from 'next/dynamic';
import AccordionBtn from 'components/commercetools-ui/atoms/accordion';
import { hasRichTextContent } from 'helpers/content/hasRichTextContent';
import { useFormat } from 'helpers/hooks/useFormat';
import { UIProductContent } from '../types';
import { AdditionalSection } from './additional-section';

const RichText = dynamic(() => import('../../../../atoms/rich-text'));

interface AdditionalInfoProps extends Omit<UIProductContent, 'description'> {
  description?: ReactNode;
}

const accordionProps = {
  className: 'border-b border-[#e8e8e8]',
  buttonClassName: 'py-16 px-8 font-medium text-16 leading-loose tracking-normal text-[#212121]',
  panelClassName: 'pb-12 px-8 text-[#5e5e5e]',
  variant: 'plusAndMinus' as const,
};

const AdditionalInfo: FC<AdditionalInfoProps> = ({
  description,
  highlights,
  whatsInTheBox,
  frequentlyAskedQuestions,
}) => {
  const { formatMessage } = useFormat({ name: 'product' });

  // Every section below is conditional, and the wrapper carries a top rule. Without this the
  // rule paints on its own for a product with no Contentful entry and no commercetools
  // description — previously unreachable, since the description always rendered.
  const hasAnySection =
    !!description ||
    hasRichTextContent(highlights) ||
    hasRichTextContent(whatsInTheBox) ||
    !!frequentlyAskedQuestions?.length;

  if (!hasAnySection) return null;

  return (
    <div className="border-t border-[#e8e8e8]">
      {description && (
        <AccordionBtn
          {...accordionProps}
          defaultOpen
          closedSectionTitle={formatMessage({ id: 'product.desc', defaultMessage: 'Product Description' })}
        >
          {description}
        </AccordionBtn>
      )}
      {hasRichTextContent(highlights) && (
        <AccordionBtn
          {...accordionProps}
          closedSectionTitle={formatMessage({ id: 'product.highlights', defaultMessage: 'Highlights' })}
        >
          <RichText content={highlights} variant="small" />
        </AccordionBtn>
      )}
      {hasRichTextContent(whatsInTheBox) && (
        <AccordionBtn
          {...accordionProps}
          closedSectionTitle={formatMessage({ id: 'product.whatsInTheBox', defaultMessage: "What's in the box" })}
        >
          <RichText content={whatsInTheBox} variant="small" />
        </AccordionBtn>
      )}
      {!!frequentlyAskedQuestions?.length && (
        <AdditionalSection title={formatMessage({ id: 'faq', defaultMessage: 'Frequently Asked Questions' })}>
          {frequentlyAskedQuestions.map((faq) => (
            <AccordionBtn
              {...accordionProps}
              key={faq.id}
              closedSectionTitle={faq.question}
            >
              <RichText content={faq.answer} variant="small" className="text-tertiary" />
            </AccordionBtn>
          ))}
        </AdditionalSection>
      )}
    </div>
  );
};

export default AdditionalInfo;
